import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSite } from "@/context/SiteContext";
import { useServerCart } from "@/lib/useServerCart";
import { addCheck, cartCount, cartEntry, syncLines, upsert, without, withQty } from "@/lib/cart";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const { settings } = useSite();
  const [items, setItems] = useServerCart(!!settings.online_payments_enabled);
  const [open, setOpen] = useState(false);

  const add = useCallback(
    (product, qty = 1) => {
      const check = addCheck(items, product, qty);
      if (check.ok) setItems((prev) => upsert(prev, cartEntry(product, check.qty)));
      return check;
    },
    [items, setItems]
  );
  const setQty = useCallback((id, qty) => setItems((prev) => withQty(prev, id, qty)), [setItems]);
  const remove = useCallback((id) => setItems((prev) => without(prev, id)), [setItems]);
  const clear = useCallback(() => setItems([]), [setItems]);
  const syncFromQuote = useCallback((lines) => setItems((prev) => syncLines(prev, lines)), [setItems]);

  const value = useMemo(
    () => ({ items, count: cartCount(items), add, setQty, remove, clear, syncFromQuote, open, setOpen }),
    [items, add, setQty, remove, clear, syncFromQuote, open]
  );
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
